import { useEffect, useRef, useState, useCallback } from "react";
import { Activity, Loader2, Lock, Radio, ShieldAlert, Skull, Swords, Heart, Users } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";

const SOURCE_STYLE = { pulse: "text-primary border-primary/30", cue: "text-amber-300 border-amber-500/30", transition: "text-secondary border-secondary/30", manual: "text-muted-foreground border-border", voice: "text-emerald-300 border-emerald-500/30" };
const CUES = ["boss", "tension", "combat", "victory", "calm"];

// PlayerPulse simulator + FeedbackLoop gauge + MetaCompoder decision log.
export default function PulsePanel({ session, onParams }) {
  const [sig, setSig] = useState({ hp: 0.8, damage_taken: 0.1, enemies_nearby: 1, deaths: 0, kills: 0 });
  const [autoMood, setAutoMood] = useState(false);
  const [auto, setAuto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [log, setLog] = useState([]);
  const [lock, setLock] = useState(false);
  const timer = useRef(null);
  const sid = session?.session_id;

  const refresh = useCallback(async () => {
    if (!sid) return;
    try { const { data } = await api.get(`/adaptive/decisions?session_id=${sid}&limit=12`); setLog(data.decisions); setLock(data.session.lock_active); } catch { /* ignore */ }
  }, [sid]);
  useEffect(() => { refresh(); }, [refresh]);

  const send = useCallback(async (s = sig) => {
    if (!sid) return;
    setBusy(true);
    try {
      const { data } = await api.post("/adaptive/pulse", { session_id: sid, ...s, auto_mood: autoMood });
      setLast(data);
      if (data.applied) onParams?.(data.params, data.mood);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Pulse failed"); }
    finally { setBusy(false); }
  }, [sid, sig, autoMood, onParams, refresh]);

  useEffect(() => {
    clearInterval(timer.current);
    if (!auto || !sid) return undefined;
    timer.current = setInterval(() => {
      setSig((s) => {
        const enemies = Math.max(0, Math.min(8, s.enemies_nearby + (Math.random() < 0.5 ? -1 : 1)));
        const dmg = Math.min(1, Math.max(0, enemies * 0.08 + (Math.random() - 0.5) * 0.2));
        const hp = Math.min(1, Math.max(0.05, s.hp - dmg * 0.25 + 0.06));
        const next = { ...s, enemies_nearby: enemies, damage_taken: Number(dmg.toFixed(2)), hp: Number(hp.toFixed(2)), kills: s.kills + (Math.random() < 0.3 ? 1 : 0), deaths: hp < 0.1 ? s.deaths + 1 : s.deaths };
        send(next);
        return next;
      });
    }, 3000);
    return () => clearInterval(timer.current);
  }, [auto, sid, send]);

  const cue = async (state) => {
    if (!sid) return;
    try {
      const { data } = await api.post("/adaptive/narrative-cue", { session_id: sid, state, hold_sec: 20 });
      onParams?.(data.params, data.mood);
      toast.success(`Narrative cue → ${state} · pulses locked out for 20 s`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Cue failed"); }
  };

  const [ducked, setDucked] = useState(false);
  const voice = async (speaking) => {
    if (!sid) return;
    try {
      const { data } = await api.post("/adaptive/voice", { session_id: sid, speaking, level: 0.35, seconds: 6 });
      setDucked(speaking); onParams?.(data.params);
      toast.success(speaking ? `Voix : musique baissée à ${Math.round(data.params.master_volume * 100)} % (DynamicMixer)` : "Voix terminée : musique restaurée");
      refresh();
      if (speaking) setTimeout(() => setDucked(false), 6000);
    } catch (e) { toast.error(e.response?.data?.detail || "Ducking failed"); }
  };
  const tension = last?.tension ?? 0;
  const set = (k) => (v) => setSig((s) => ({ ...s, [k]: v }));
  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="pulse-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold flex items-center gap-2"><Activity size={16} className="text-primary" /> PlayerPulse <span className="text-xs text-muted-foreground font-normal">— gameplay signals → tension → intensity (FeedbackLoop), narrative cues keep priority (MetaCompoder)</span></h3>
        {lock && <Badge variant="outline" className="text-amber-300 border-amber-500/30 gap-1" data-testid="pulse-lock-badge"><Lock size={11} /> narrative cue holds priority</Badge>}
      </div>
      {!sid ? <p className="text-xs text-muted-foreground">Waiting for the adaptive session…</p> : (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Row icon={Heart} label="HP" value={`${Math.round(sig.hp * 100)}%`}><Slider value={[sig.hp]} min={0} max={1} step={0.01} onValueChange={([v]) => set("hp")(v)} data-testid="pulse-hp" /></Row>
              <Row icon={ShieldAlert} label="Damage taken" value={`${Math.round(sig.damage_taken * 100)}%`}><Slider value={[sig.damage_taken]} min={0} max={1} step={0.01} onValueChange={([v]) => set("damage_taken")(v)} data-testid="pulse-damage" /></Row>
              <Row icon={Users} label="Enemies nearby" value={sig.enemies_nearby}><Slider value={[sig.enemies_nearby]} min={0} max={10} step={1} onValueChange={([v]) => set("enemies_nearby")(v)} data-testid="pulse-enemies" /></Row>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => set("deaths")(sig.deaths + 1)} data-testid="pulse-death"><Skull size={12} /> Death ({sig.deaths})</Button>
                <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => set("kills")(sig.kills + 1)} data-testid="pulse-kill"><Swords size={12} /> Kill ({sig.kills})</Button>
                <label className="flex items-center gap-1.5 ml-auto"><input type="checkbox" checked={autoMood} onChange={(e) => setAutoMood(e.target.checked)} data-testid="pulse-auto-mood" /> auto mood</label>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => send()} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5" data-testid="pulse-send">{busy ? <Loader2 size={13} className="animate-spin" /> : <Radio size={13} />} Send pulse</Button>
                <Button size="sm" variant={auto ? "destructive" : "outline"} onClick={() => setAuto((a) => !a)} data-testid="pulse-auto">{auto ? "Stop simulation" : "Simulate gameplay (3 s)"}</Button>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Tension score</span><span className="font-mono" data-testid="pulse-tension">{tension.toFixed(2)}</span></div>
                <div className="h-3 rounded-full bg-muted overflow-hidden"><div className="h-full bg-gradient-to-r from-sky-400 via-amber-400 to-red-500 transition-[width] duration-500" style={{ width: `${tension * 100}%` }} /></div>
                {last && <p className="text-[11px] text-muted-foreground mt-1" data-testid="pulse-last">{last.applied ? `→ intensity ${Number(last.intensity).toFixed(2)}` : "ignored"}{last.suggested_mood ? ` · suggested mood: ${last.suggested_mood}` : ""} · {last.decision?.reason}</p>}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1.5">Narrative cue (locks pulses 20 s)</p>
                <div className="flex flex-wrap gap-1.5">{CUES.map((s) => <button key={s} type="button" onClick={() => cue(s)} className="px-2.5 py-1 rounded-md border border-border text-xs capitalize text-muted-foreground hover:text-amber-300 hover:border-amber-500/40" data-testid={`narrative-cue-${s}`}>{s}</button>)}</div>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1.5">DynamicMixer · voice ducking</p>
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => voice(true)} className={`px-2.5 py-1 rounded-md border text-xs ${ducked ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`} data-testid="voice-duck-on">NPC speaks (6 s)</button>
                  <button type="button" onClick={() => voice(false)} className="px-2.5 py-1 rounded-md border border-border text-xs text-muted-foreground hover:text-foreground" data-testid="voice-duck-off">Voice ended</button>
                </div>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1.5">Decision log</p>
                <ul className="space-y-1 max-h-40 overflow-auto pr-1" data-testid="decision-log">
                  {log.length === 0 && <li className="text-xs text-muted-foreground">No decision yet.</li>}
                  {log.map((d) => (
                    <li key={d.id} className="text-[11px] flex items-start gap-2" data-testid={`decision-${d.id}`}>
                      <Badge variant="outline" className={`h-4 px-1 text-[9px] uppercase ${SOURCE_STYLE[d.source] || ""}`}>{d.source}</Badge>
                      <span className={`font-mono ${d.applied ? "" : "line-through opacity-60"}`}>{d.intensity_from.toFixed(2)}→{d.intensity_to.toFixed(2)}</span>
                      <span className="text-muted-foreground flex-1 truncate" title={d.reason}>{d.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, children }) {
  return (
    <div>
      <div className="flex justify-between text-xs text-muted-foreground mb-1"><span className="flex items-center gap-1"><Icon size={11} /> {label}</span><span className="font-mono">{value}</span></div>
      {children}
    </div>
  );
}
